import { Payment } from './types';
import { logError } from '@/lib/sentry';

// 결제창 요청 파라미터 타입
interface PortOnePaymentParams {
  storeId: string;
  channelKey: string;
  paymentId: string;
  orderName: string;
  totalAmount: number;
  currency: 'KRW';
  payMethod: 'CARD' | 'VIRTUAL_ACCOUNT' | 'MOBILE_PHONE' | 'TRANSFER' | 'EASY_PAY' | 'GIFT_CERTIFICATE';
  redirectUrl: string;
  customer: {
    id: string;
    name: string;
    email: string;
  };
  bypass?: {
    cardQuota?: number[];
    cashReceiptType?: 'PERSONAL' | 'BUSINESS' | 'NONE';
  };
}

// 빌링키 발급 요청 파라미터 타입
interface BillingKeyParams {
  storeId: string;
  channelKey?: string;
  customerKey: string;  // 고객 식별자
  billingKeyMethod: 'CARD';
  displayAmount?: number;
  currency?: 'KRW' | 'USD' | 'EUR' | 'JPY';
  issueName?: string;
  issueId?: string;
  customer?: {
    customerId?: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
    address?: {
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      province?: string;
      zipcode?: string;
    };
    gender?: 'MALE' | 'FEMALE';
    birthYear?: string;
    birthMonth?: string;
    birthDay?: string;
  };
  windowType?: {
    pc?: 'IFRAME' | 'POPUP' | 'REDIRECTION';
    mobile?: 'IFRAME' | 'POPUP' | 'REDIRECTION';
  };
  redirectUrl?: string;
  locale?: 'ko' | 'en' | 'jp' | 'zh';
  customData?: any;
  noticeUrls?: string[];
  bypass?: {
    card?: {
      cardQuota?: number[];
      cardIssuerCode?: string;
    }
  };
}

// 빌링키 발급 응답 타입
interface BillingKeyResponse {
  transactionType: 'ISSUE_BILLING_KEY';
  billingKey?: string;
  customerKey: string;
  method: string;
  code?: string;  // 에러 시 존재
  message?: string;  // 에러 메시지
  pgCode?: string; // PG 오류 코드
  pgMessage?: string; // PG 오류 메시지
}

// Payment 타입과 일치하도록 상태값 통일
type PaymentStatus = Payment['status'];

interface PortOnePaymentResponse {
  paymentKey: string;
  orderId: string;
  orderName: string;
  status: PaymentStatus;
  totalAmount: number;
  balanceAmount: number;
  requestedAt: string;
  approvedAt?: string;
  method: string;
  currency: string;
}

// 결제 검증 응답 타입
interface PaymentVerificationResponse {
  status: PaymentStatus;
  paymentKey: string;
  orderId: string;
  orderName: string;
  totalAmount: number;
  balanceAmount: number;
  method: string;
  requestedAt: string;
  approvedAt?: string;
  currency: string;
  receipt?: {
    url: string;
  };
  checkout?: {
    url: string;
  };
  country: string;
  failure?: {
    code: string;
    message: string;
  };
}

// 결제 취소 응답 타입
interface PaymentCancellationResponse {
  status: PaymentStatus;
  paymentKey: string;
  orderId: string;
  orderName: string;
  totalAmount: number;
  balanceAmount: number;
  cancelAmount: number;
  cancelReason: string;
  canceledAt: string;
  method: string;
  currency: string;
  receipt?: {
    url: string;
  };
}

declare global {
  interface Window {
    PortOne: {
      requestPayment: (params: PortOnePaymentParams) => Promise<PortOnePaymentResponse>;
      requestIssueBillingKey: (params: BillingKeyParams) => Promise<BillingKeyResponse>;
    };
  }
}

// 환경변수 상수 정의
const PORTONE_STORE_ID = import.meta.env.VITE_PORTONE_STORE_ID!;
const PORTONE_CHANNEL_KEY = import.meta.env.VITE_PORTONE_CHANNEL_KEY!;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL!;
const PORTONE_SDK_URL = 'https://cdn.portone.io/v2/browser-sdk.js';
const PORTONE_SDK_CHECK_INTERVAL_MS = 100;
// const CARD_QUOTA_SINGLE_PAYMENT = [0]; // 일시불만 허용

let sdkReady = false;

/**
 * PortOne SDK를 한 번만 로드합니다.
 * 이미 로드된 경우 즉시 반환합니다.
 */
export async function loadPortOneSDK(): Promise<void> {
  // 브라우저 환경 체크
  if (typeof window === 'undefined') {
    throw new Error('브라우저 환경에서만 실행 가능합니다');
  }
  
  // 이미 로드된 경우 즉시 반환
  if (sdkReady) return;

  // 전역 객체에 PortOne이 없는 경우 스크립트 로드
  if (!window.PortOne) {
    await new Promise<void>((resolve, reject) => {
      // 이미 script 태그가 있는지 확인
      const existingScript = document.querySelector(`script[src*="${PORTONE_SDK_URL}"]`);
      
      if (existingScript) {
        const checkSDK = () => {
          if (window.PortOne) {
            sdkReady = true;
            resolve();
          } else {
            setTimeout(checkSDK, PORTONE_SDK_CHECK_INTERVAL_MS);
          }
        };
        checkSDK();
        return;
      }

      // 새로운 스크립트 태그 생성 및 로드
      const script = document.createElement('script');
      script.src = PORTONE_SDK_URL;
      script.onload = () => {
        if (window.PortOne) {
          sdkReady = true;
          resolve();
        } else {
          reject(new Error('PortOne SDK가 제대로 로드되지 않았습니다'));
        }
      };
      script.onerror = () => reject(new Error('PortOne SDK 로드 실패'));
      document.body.appendChild(script);
    });
  } else {
    sdkReady = true;
  }
}

/**
 * 빌링키 발급 요청
 * @param userId 사용자 ID (customerKey로 사용)
 * @param customRedirectUrl 커스텀 리다이렉트 URL (옵션)
 * @returns 빌링키 응답
 */
export async function issueBillingKey(
  userId: string,
  customRedirectUrl?: string
): Promise<BillingKeyResponse> {
  try {
    // SDK 로드 확인
    await loadPortOneSDK();

    // 리디렉트 URL 생성 (커스텀 URL이 제공되었으면 사용, 그렇지 않으면 기본값)
    const redirectUrl = customRedirectUrl || `${window.location.origin}/payment/callback`;
    
    // 빌링키 발급 요청
    const response = await window.PortOne.requestIssueBillingKey({
      storeId: PORTONE_STORE_ID,
      channelKey: PORTONE_CHANNEL_KEY,
      customerKey: userId,
      billingKeyMethod: 'CARD',
      redirectUrl: redirectUrl,
      windowType: {
        pc: 'IFRAME',
        mobile: 'REDIRECTION', // 모바일에서는 리다이렉션 사용하도록
      },
    });

    console.log("🔥 빌링키 발급 요청 완료")

    // 에러 코드가 있는 경우
    if (response.code !== undefined) {
      logError('빌링키 발급 실패:', response.message);
      
      // 사용자가 결제창을 닫은 경우를 특별하게 처리
      if (response.code === 'PAY_PROCESS_CANCELED' || 
          (response.message && response.message.includes('사용자가 결제를 취소'))) {
        const userCancelError = new Error(response.message || '사용자가 결제를 취소했습니다');
        userCancelError.name = 'UserCancelError';
        throw userCancelError;
      }
      
      throw new Error(response.message || '빌링키 발급 중 오류가 발생했습니다');
    }

    return response;
  } catch (error) {
    logError('빌링키 발급 중 오류 발생:', error);
    
    // 사용자 취소 에러가 이미 처리되었으면 그대로 전달
    if (error instanceof Error && error.name === 'UserCancelError') {
      throw error;
    }
    
    // 그 외 에러는 일반적인 에러 메시지로 변환
    const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다';
    
    // PAY_PROCESS_CANCELED 메시지가 포함된 경우 사용자 취소 에러로 변환
    if (errorMessage.includes('PAY_PROCESS_CANCELED') || 
        errorMessage.includes('사용자가 결제를 취소')) {
      const userCancelError = new Error('사용자가 결제를 취소했습니다');
      userCancelError.name = 'UserCancelError';
      throw userCancelError;
    }
    
    throw new Error('카드 등록 중 오류가 발생했습니다');
  }
}

/**
 * 결제 상태 확인 (Edge Function 호출)
 * @param paymentId 포트원 결제 ID
 */
export async function verifyPayment(paymentId: string): Promise<PaymentVerificationResponse> {
  try {
    // 결제 검증을 위한 API 호출
    const response = await fetch(`${SUPABASE_URL}/functions/v1/payment-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ paymentId }),
    });

    if (!response.ok) {
      throw new Error(`결제 검증 API 응답 오류: ${response.status} ${response.statusText}`);
    }

    const verificationResult = await response.json();
    return verificationResult;
  } catch (error) {
    logError('결제 검증 중 오류 발생:', error);
    throw error;
  }
}

/**
 * 결제 취소 요청 (Edge Function 호출)
 * @param paymentId 포트원 결제 ID
 * @param reason 취소 사유
 */
export async function cancelPayment(paymentId: string, reason: string): Promise<PaymentCancellationResponse> {
  try {
    // 결제 취소를 위한 API 호출
    const response = await fetch(`${SUPABASE_URL}/functions/v1/payment-cancellation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        paymentId,
        cancelReason: reason || '사용자 요청에 의한 취소'
      }),
    });

    if (!response.ok) {
      throw new Error(`결제 취소 API 응답 오류: ${response.status} ${response.statusText}`);
    }

    const cancellationResult = await response.json();
    return cancellationResult;
  } catch (error) {
    logError('결제 취소 중 오류 발생:', error);
    throw error;
  }
} 